import { indexBy } from "diagnostics-lib/iterable";
import { useManualFailures, type ManualFailure } from "./useManualFailures";
import { useCallback, useMemo } from "react";
import useActiveAnalysesStream from "./useActiveAnalysesStream";
import { useQuery } from "@tanstack/react-query";
import usePerformanceIndicators, { type PerformanceIndicator } from "./usePerformanceIndicators";
import {
  getManualFaultEventActive,
  getManualFaultEventEndsAt,
  getManualFaultEventStatus,
} from "diagnostics-domain/manualFaultEvent";
import { MINUTE_MS } from "@ui/i18n/formatDate";
import { useGet } from "./useCall";
import useDistinctShape from "@ui/lib/hooks/useDistinctShape";
import {
  MANUAL_FAULT_EVENTS_PATH as PATH,
  useManualFaultEventsChannel,
} from "./useManualFaultEventsChannel";

export function useRemoteManualFaultEvents(projectId: string, enabled: boolean) {
  const queryKey = useMemo(() => [PATH, projectId], [projectId]);
  const getRemoteManualFaultEvents = useGet(PATH);
  const queryFn = useCallback(
    () => getRemoteManualFaultEvents({ routeParams: { projectId } }),
    [projectId, getRemoteManualFaultEvents],
  );

  const query = useQuery({
    queryKey,
    queryFn,
    enabled,
    keepPreviousData: false,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    suspense: false,
    staleTime: 200 * MINUTE_MS,
  });

  useManualFaultEventsChannel(projectId);

  const body = useDistinctShape(query.data?.body);
  return useMemo(() => body ?? [], [body]);
}

export function useManualFaultEvents(projectId: string, enabled: boolean) {
  const manualFaultEvents = useRemoteManualFaultEvents(projectId, enabled);
  const manualFailures = useManualFailures(projectId);

  const indexedManualFailures = useMemo(() => indexBy(manualFailures, "id"), [manualFailures]);
  const { activeAnalyses } = useActiveAnalysesStream(projectId);
  const activeAnalysisDates = useMemo(
    () => activeAnalyses.map(a => new Date(a.date)),
    [activeAnalyses],
  );

  const performanceIndicators = usePerformanceIndicators(projectId);

  return useMemo(
    () =>
      manualFaultEvents.flatMap(
        manualFaultEvent =>
          mapRemoteManualFaultEvent({
            manualFaultEvent,
            indexedManualFailures,
            activeAnalysisDates,
            performanceIndicators,
          }) ?? [],
      ),
    [manualFaultEvents, indexedManualFailures, activeAnalysisDates, performanceIndicators],
  );
}

export type RemoteManualFaultEvent = ReturnType<typeof useRemoteManualFaultEvents>[number];
export type ManualFaultEvent = ReturnType<typeof useManualFaultEvents>[number];

export function mapRemoteManualFaultEvent({
  manualFaultEvent,
  indexedManualFailures,
  activeAnalysisDates,
  performanceIndicators,
}: {
  manualFaultEvent: RemoteManualFaultEvent;
  indexedManualFailures: Map<string, ManualFailure>;
  activeAnalysisDates: Date[];
  performanceIndicators: PerformanceIndicator[];
}) {
  {
    const manualFault = indexedManualFailures.get(manualFaultEvent.manualFaultId);
    if (!manualFault) {
      console.debug(
        `Manual fault event ${manualFaultEvent.id} references a manual fault that does not exist: ${manualFaultEvent.manualFaultId}`,
      );
      return;
    }

    const systemId = manualFault.systemId;
    const closedAt = manualFaultEvent.closedAt ? new Date(manualFaultEvent.closedAt) : null;

    const endsAt = getManualFaultEventEndsAt(
      new Date(manualFaultEvent.startsAt),
      closedAt,
      activeAnalysisDates,
    );

    if (endsAt === null) return;

    const windows = manualFaultEvent.manualFaultImpactTimeWindows.flatMap(
      (currentWindow, index, all) => {
        if (!currentWindow) return [];

        const nextWindow = all[index + 1];
        const startsAt = new Date(currentWindow.startsAt);

        const windowEndsAt = nextWindow?.startsAt
          ? (() => {
              const d = new Date(nextWindow.startsAt);
              d.setDate(d.getDate() - 1);
              return d;
            })()
          : new Date(endsAt);

        return [
          {
            id: currentWindow.id,
            manualFaultEventId: manualFaultEvent.id,
            startsAt,
            endsAt: windowEndsAt,
          },
        ];
      },
    );

    const impacts = manualFaultEvent.manualFaultImpactTimeWindows.flatMap(w =>
      performanceIndicators.map(pi => {
        const impact = w.manualFaultImpacts.find(impact => impact.quantityId === pi.id);

        return {
          manualFaultImpactTimeWindowId: w.id,
          quantityId: pi.id,
          quantityName: pi.name,
          unit: pi.unit,
          value: impact?.value ?? 0,
        };
      }),
    );

    return {
      id: manualFaultEvent.id,
      name: manualFaultEvent.name,
      description: manualFaultEvent.description,
      manualFaultId: manualFaultEvent.manualFaultId,
      priority: manualFaultEvent.priority,
      startsAt: new Date(manualFaultEvent.startsAt),
      closedAt,
      windows,
      impacts,
      identifiedAction: manualFaultEvent.identifiedAction,
      reparability: manualFaultEvent.reparability,
      updatedAt: manualFaultEvent.updatedAt,
      projectId: manualFaultEvent.projectId,
      metadata: {
        manualFaultName: manualFault.name,
        status: getManualFaultEventStatus(
          manualFaultEvent.closedAt ? new Date(manualFaultEvent.closedAt) : null,
          manualFaultEvent.identifiedActionAt
            ? new Date(manualFaultEvent.identifiedActionAt)
            : null,
        ),
        endsAt,
        active: getManualFaultEventActive(
          new Date(manualFaultEvent.updatedAt),
          closedAt,
          endsAt,
          activeAnalysisDates.length,
        ),
        manualFault: {
          id: manualFault.id,
          name: manualFault.name,
          systemId,
        },
      },
    };
  }
}
